'use client'
import Image from 'next/image'
import { Activity, Map as MapIcon, MessageCircle } from 'lucide-react'
import { mapPoints, mapRadius } from './world-map-data'
import { Area, AreaChart, CartesianGrid } from 'recharts'
import { type ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart'
import { useTranslations, useLocale } from 'next-intl'

export default function FeaturesSection() {
    const t = useTranslations('marketing.featuresTailark')
    const locale = useLocale()
    const isRtl = locale === 'ar'

    return (

        <section className="py-12 md:py-20">
            <div className="max-w-7xl mx-auto w-full px-4 sm:px-6">
                <div className="mx-auto grid w-full border md:grid-cols-2 bg-background rounded-xl overflow-hidden shadow-sm">
                    {/* Visitor Map Section */}
                    <div className="flex flex-col">
                        <div className="p-8 sm:p-16 flex flex-col items-center text-center md:items-start md:text-start">
                            <span className="text-muted-foreground flex items-center gap-2">
                                <MapIcon className="size-4" />
                                {t('map.title')}
                            </span>
                            <p className="mt-4 sm:mt-8 text-lg sm:text-2xl font-semibold">{t('map.desc')}</p>
                        </div>

                        <div aria-hidden className="relative w-full h-[250px] sm:h-[350px] overflow-hidden">
                            <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 z-20 flex justify-center px-4 w-full cursor-none pointer-events-none">
                                <div className="max-w-full inline-flex items-center gap-2 rounded-full bg-background dark:bg-muted/90 border px-3 py-1.5 text-xs font-medium shadow-xl shadow-zinc-950/10">
                                    <div className="flex items-center gap-2 shrink-0">
                                        <span className="relative flex h-2.5 w-2.5 sm:h-3 sm:w-3 shrink-0">
                                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary/80 opacity-75"></span>
                                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 sm:h-3 sm:w-3 bg-primary"></span>
                                        </span>
                                        <span className="shrink-0 flex items-center justify-center">
                                            <img
                                                src="https://flagcdn.com/dz.svg"
                                                alt="Algeria Flag"
                                                className="rounded-[2px] object-cover h-[12px] w-[16px] sm:h-[15px] sm:w-[20px]"
                                            />
                                        </span>
                                    </div>
                                    <span className="truncate max-w-[150px] xs:max-w-none pt-0.5" dir="auto">{t('map.badge', { location: isRtl ? 'وهران' : 'Oran' })}</span>
                                </div>
                            </div>

                            <div className="absolute inset-0 z-10 w-full h-full flex items-center justify-center pointer-events-none">
                                <div className="bg-radial z-[1] to-background absolute inset-0 from-transparent to-75%"></div>
                                <div className="w-[120%] sm:w-full h-full flex items-center justify-center -ml-[10%] sm:ml-0">
                                    <Map />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Support Section */}
                    <div className="overflow-hidden border-t md:border-t-0 bg-zinc-50 p-5 sm:p-12 md:border-l rtl:md:border-l-0 rtl:md:border-r border-zinc-200 dark:border-zinc-800 dark:bg-muted/30 flex flex-col">
                        <div className="relative z-10 flex flex-col items-center text-center md:items-start md:text-start">
                            <span className="text-muted-foreground flex items-center gap-2">
                                <MessageCircle className="size-4" />
                                {t('support.title')}
                            </span>
                            <p className="my-4 sm:my-8 text-lg sm:text-2xl font-semibold text-pretty max-w-lg">{t('support.desc')}</p>
                        </div>

                        <div aria-hidden className="flex flex-col gap-4 sm:gap-8 mt-4 sm:mt-0 w-full max-w-sm md:max-w-none mx-auto md:mx-0">
                            <div className="w-full">
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="size-5 rounded-full border bg-background overflow-hidden relative shrink-0">
                                        <Image
                                            src="/logo-light.png"
                                            alt="Ecommaps Logo"
                                            width={20}
                                            height={20}
                                            className="h-full w-full object-cover"
                                        />
                                    </span>
                                    <span className="text-muted-foreground text-xs">{t('support.badgeTime')}</span>
                                </div>
                                <div className="rounded-[var(--radius)] bg-background border p-3 text-xs shadow-sm w-[85%] sm:w-3/5 mr-auto rtl:mr-0 rtl:ml-auto">
                                    {t('support.messageUser')}
                                </div>
                            </div>

                            <div className="w-full">
                                <div className="rounded-[var(--radius)] bg-primary p-3 text-xs text-primary-foreground shadow-sm w-[85%] sm:w-3/5 ml-auto rtl:ml-0 rtl:mr-auto mb-1">
                                    {t('support.messageSupport')}
                                </div>
                                <span className="text-muted-foreground block text-right rtl:text-left text-xs">{t('support.badgeAvailable')}</span>
                            </div>
                        </div>
                    </div>

                    {/* Analytics Section */}
                    <div className="col-span-full border-y p-6 sm:p-12 bg-background">
                        <p className="text-center text-2xl xs:text-3xl sm:text-4xl lg:text-7xl font-semibold leading-tight text-balance px-2">{t('uptime.title')}</p>
                        <p className="text-center text-muted-foreground mt-4 text-sm sm:text-lg max-w-2xl mx-auto px-4 text-balance">{t('uptime.desc')}</p>
                    </div>

                    <div className="relative col-span-full bg-background h-[300px] sm:h-[400px] overflow-hidden" dir="ltr">
                        <div className="absolute z-10 w-full px-6 pt-6 md:px-12 md:pt-12 top-0 pointer-events-none md:text-left text-center md:left-0" dir={isRtl ? 'rtl' : 'ltr'}>
                            <div className={`flex flex-col items-center gap-1 max-w-full ${isRtl ? 'md:items-end' : 'md:items-start'}`}>
                                <span className="text-muted-foreground flex items-center justify-center md:justify-start gap-2 text-sm font-medium">
                                    <Activity className="size-4" />
                                    {t('uptime.feed')}
                                </span>
                                <p className="text-xl sm:text-2xl font-semibold leading-tight whitespace-nowrap md:whitespace-normal overflow-hidden text-ellipsis w-full max-w-[280px] sm:max-w-md">
                                    {t('uptime.instantly')}
                                    <span className="text-muted-foreground block mt-1 sm:mt-0"> {t('uptime.resolve')}</span>
                                </p>
                            </div>
                        </div>

                        <div className="w-full h-full">
                            <MonitoringChart />
                        </div>
                    </div>
                </div>
            </div>
        </section >
    )
}


// Pre-computed map data - no runtime dotted-map dependency needed
const Map = () => {
    return (
        <svg
            viewBox="0 0 120 60"
            className="w-full h-full text-black dark:text-white"
            style={{ background: 'transparent' }}>
            {mapPoints.map(([cx, cy], i) => (
                <circle
                    key={i}
                    cx={cx}
                    cy={cy}
                    r={mapRadius}
                    fill="currentColor"
                />
            ))}
        </svg>
    )
}

const chartConfig = {
    desktop: {
        label: 'Desktop',
        color: 'hsl(var(--primary))',
    },
    mobile: {
        label: 'Mobile',
        color: 'hsl(var(--primary))',
    },
} satisfies ChartConfig

const chartData = [
    { month: 'May', desktop: 56, mobile: 224 },
    { month: 'June', desktop: 56, mobile: 224 },
    { month: 'January', desktop: 126, mobile: 252 },
    { month: 'February', desktop: 205, mobile: 410 },
    { month: 'March', desktop: 200, mobile: 126 },
    { month: 'April', desktop: 400, mobile: 800 },
]

const MonitoringChart = () => {
    return (
        <ChartContainer
            className="h-[400px] w-full"
            config={chartConfig}>
            <AreaChart
                accessibilityLayer
                data={chartData}
                margin={{
                    left: 0,
                    right: 0,
                    top: 0,
                    bottom: 0,
                }}>
                <defs>
                    <linearGradient
                        id="fillDesktop"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1">
                        <stop
                            offset="0%"
                            stopColor="hsl(var(--primary))"
                            stopOpacity={0.8}
                        />
                        <stop
                            offset="55%"
                            stopColor="hsl(var(--primary))"
                            stopOpacity={0.1}
                        />
                    </linearGradient>
                    <linearGradient
                        id="fillMobile"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1">
                        <stop
                            offset="0%"
                            stopColor="hsl(var(--primary))"
                            stopOpacity={0.4}
                        />
                        <stop
                            offset="55%"
                            stopColor="hsl(var(--primary))"
                            stopOpacity={0.05}
                        />
                    </linearGradient>
                </defs>
                <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="currentColor" strokeOpacity={0.1} />
                <ChartTooltip
                    active
                    cursor={false}
                    content={<ChartTooltipContent className="dark:bg-muted" />}
                />
                <Area
                    strokeWidth={2}
                    dataKey="mobile"
                    type="stepBefore"
                    fill="url(#fillMobile)"
                    fillOpacity={0.1}
                    stroke="hsl(var(--primary))"
                    stackId="a"
                />
                <Area
                    strokeWidth={2}
                    dataKey="desktop"
                    type="stepBefore"
                    fill="url(#fillDesktop)"
                    fillOpacity={0.1}
                    stroke="hsl(var(--primary))"
                    stackId="a"
                />
            </AreaChart>
        </ChartContainer>
    )
}
